import { useState, useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { mockVideos, categories } from "@/mocks/videos";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCat, setActiveCat] = useState(slug || "hot");
  const [sortBy, setSortBy] = useState<"latest" | "popular">("latest");

  useEffect(() => {
    if (slug) setActiveCat(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeCat]);

  const currentCategory = categories.find((c) => c.slug === activeCat);

  const filteredVideos = useMemo(() => {
    let videos = mockVideos.filter((v) => v.category === activeCat);
    if (sortBy === "latest") {
      videos = [...videos].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else {
      videos = [...videos].sort((a, b) => b.views - a.views);
    }
    return videos;
  }, [activeCat, sortBy]);

  return (
    <main className="pt-16">
      <div className="bg-background-100 border-b border-background-200/60">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <nav className="flex items-center gap-1 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">{currentCategory?.name || "分类"}</span>
          </nav>

          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
            {currentCategory?.name || "视频分类"}
          </h1>
          <p className="text-sm text-foreground-500">
            浏览{currentCategory?.name}相关的所有视频内容
          </p>
        </div>
      </div>

      <div className="sticky top-16 z-30 bg-background-50 border-b border-background-200/60">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center gap-1 overflow-x-auto scrollbar-hide py-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              onClick={() => setActiveCat(cat.slug)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeCat === cat.slug
                  ? "bg-primary-500 text-white"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-2 flex items-center gap-2">
          <button
            onClick={() => setSortBy("latest")}
            className={`text-xs px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${
              sortBy === "latest" ? "bg-foreground-900 text-background-50" : "text-foreground-500 hover:bg-background-100"
            }`}
          >
            最新
          </button>
          <button
            onClick={() => setSortBy("popular")}
            className={`text-xs px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${
              sortBy === "popular" ? "bg-foreground-900 text-background-50" : "text-foreground-500 hover:bg-background-100"
            }`}
          >
            最热
          </button>
        </div>
      </div>

      <section className="py-6 md:py-8 px-4 md:px-6 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          {filteredVideos.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-foreground-500">该分类暂无视频内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 bg-black/65 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                      {video.duration}
                    </div>
                    <div className="absolute bottom-2 left-2">
                      <span className="bg-primary-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                        {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views.toLocaleString()} 观看
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-foreground-500">{video.date}</span>
                      {video.tags && video.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-[10px] text-secondary-700 bg-secondary-100 px-1.5 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}